/**
 * Cheribundi Recovery Bundle landing page behaviour.
 *
 * A single 12 / 24 portion state drives every price spot, every add-to-cart
 * form and the offer popup at once. The state is nothing but `data-portion` on
 * the document element; the prices themselves are all rendered server side and
 * only shown / hidden by CSS (see assets/cheribundi-bundle.css). JavaScript's
 * only jobs are:
 *   1. flip `data-portion` when a toggle button is pressed,
 *   2. copy the matching variant id into every add-to-cart form's hidden input,
 *   3. keep every toggle instance's pressed state in sync,
 *   4. open / close the offer popup.
 */

const PORTIONS = ['12', '24'];
const DEFAULT_PORTION = '12';

/** @type {Set<CheribundiPortionToggle>} */
const toggles = new Set();

let currentPortion = DEFAULT_PORTION;

/**
 * Applies a portion to the whole page: the document attribute (CSS keys off
 * it), every add-to-cart hidden id input, and every toggle's pressed state.
 * @param {string} portion
 */
function applyPortion(portion) {
  if (!PORTIONS.includes(portion)) return;
  currentPortion = portion;
  document.documentElement.dataset.portion = portion;

  for (const input of document.querySelectorAll('[data-cheribundi-id]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const value = input.getAttribute(`data-id-${portion}`);
    if (value) input.value = value;
  }

  for (const toggle of toggles) toggle.reflect(portion);
}

/**
 * `<cheribundi-portion-toggle>` — two buttons that set the shared portion.
 * Multiple instances stay in sync because they all read / write the module
 * state above.
 */
class CheribundiPortionToggle extends HTMLElement {
  connectedCallback() {
    toggles.add(this);
    this.addEventListener('click', this.#handleClick);
    // Adopt whatever the shared state already is (defaults to 12).
    this.reflect(currentPortion);
    // Make sure hidden inputs added around the same time get seeded.
    applyPortion(currentPortion);
  }

  disconnectedCallback() {
    toggles.delete(this);
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-portion]') : null;
    if (!(button instanceof HTMLElement)) return;
    const portion = button.dataset.portion;
    if (portion) applyPortion(portion);
  };

  /**
   * Reflects a portion onto this instance's buttons.
   * @param {string} portion
   */
  reflect(portion) {
    for (const button of this.querySelectorAll('[data-portion]')) {
      if (!(button instanceof HTMLElement)) continue;
      button.setAttribute('aria-pressed', button.dataset.portion === portion ? 'true' : 'false');
    }
  }
}

/**
 * `<cheribundi-bundle-popup>` — thin wrapper around a native <dialog>. Opens
 * when any `[data-cheribundi-popup-open]` control anywhere on the page is
 * clicked; closes on the close button, the decline link, Escape (native) and a
 * click on the backdrop.
 */
class CheribundiBundlePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this.#openers = Array.from(document.querySelectorAll('[data-cheribundi-popup-open]'));
    for (const opener of this.#openers) opener.addEventListener('click', this.#open);

    for (const closer of this.querySelectorAll('[data-cheribundi-popup-close]')) {
      closer.addEventListener('click', this.#close);
    }

    this.dialog.addEventListener('click', this.#handleBackdrop);
  }

  disconnectedCallback() {
    for (const opener of this.#openers ?? []) opener.removeEventListener('click', this.#open);
  }

  /** @type {Element[]} */
  #openers = [];

  #open = (event) => {
    event?.preventDefault?.();
    if (this.dialog instanceof HTMLDialogElement && !this.dialog.open) this.dialog.showModal();
  };

  #close = () => {
    if (this.dialog instanceof HTMLDialogElement && this.dialog.open) this.dialog.close();
  };

  /** @param {MouseEvent} event */
  #handleBackdrop = (event) => {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    // A click on the dialog element itself (outside its content box) is the backdrop.
    if (event.target === this.dialog) this.dialog.close();
  };
}

const defineElement = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

defineElement('cheribundi-portion-toggle', CheribundiPortionToggle);
defineElement('cheribundi-bundle-popup', CheribundiBundlePopup);

// Seed the default so hidden inputs are correct even if no toggle is present.
applyPortion(currentPortion);
